import React, { useEffect, useRef, useState } from 'react';

/**
 * The hero film: 32 seconds, silent, looping, built in code (../../motion,
 * STORYBOARD.md there) - your bells, today's session, the rest ring with Bo
 * meditating, the Watch, the workout saved with Bo's kick, progress, the
 * crew, and the hero's line again. It sits right under the hero, full width.
 *
 * Two cuts of one timeline: 16:9 for wide screens, and a 9:16 portrait cut
 * for phones (headline on top, the device below), chosen by the same 760px
 * breakpoint App.css uses for the frame's shape, and swapped if the window
 * crosses it.
 *
 * Decorative: everything the film says, the page says in words (the hero,
 * How it works, Crews, Apple Watch), so it is hidden from assistive
 * technology and takes no focus.
 *
 * What a visitor gets:
 * - first paint is a still, the film's END CARD - Bo, "Train solo. Get
 *   stronger together.", and where the app is coming - one small image;
 * - on a wide screen the still is NOT lazy-loaded: it is the page's largest
 *   paint there, and loading="lazy" held its request back (Lighthouse
 *   desktop: 133 ms of resource load delay). On a phone it stays lazy: the
 *   headline is the largest paint, and an eager still took bandwidth from
 *   it (Lighthouse mobile, five runs each: LCP median 2354 ms eager,
 *   1886 ms lazy);
 * - the video is mounted only after the page has loaded AND the frame is
 *   near the screen (a visitor landing on /#waitlist never downloads it),
 *   muted, inline and looping, and fades in over the still once it actually plays (its first
 *   frame is the bare chalk ground, its `poster`);
 * - with `prefers-reduced-motion: reduce` (or a browser that cannot say)
 *   the video is never mounted, and goes away if the setting changes while
 *   the page is open: the end card is the whole film;
 * - if the browser refuses to play it, the still simply stays.
 *
 * The files are served by the landing itself (public/media/), never a
 * third-party host. motion/render/encode.sh writes them.
 */
export const PHONE = '(max-width: 760px)';

const M = '/media/bell-club-hero';
export const FILM = {
  wide: {
    webm: `${M}-1080p.webm`,
    mp4: `${M}-1080p.mp4`,
    poster: `${M}-poster.jpg`,
    endcard: `${M}-endcard.jpg`,
    width: 1920,
    height: 1080,
  },
  portrait: {
    webm: `${M}-portrait.webm`,
    mp4: `${M}-portrait.mp4`,
    poster: `${M}-portrait-poster.jpg`,
    endcard: `${M}-portrait-endcard.jpg`,
    width: 720,
    height: 1280,
  },
};

const REDUCE = '(prefers-reduced-motion: reduce)';

const query = (q) =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(q)
    : null;

/**
 * Reports whether the frame is within a screen of the viewport, every time
 * that changes. It has to keep reporting: a visitor arriving at /#waitlist
 * is at the top of the page for one frame before the jump to the form, and
 * a one-shot "it was near once" would start the film for everyone. Where the
 * browser has no IntersectionObserver: near, at once (as before it existed).
 */
export const DWELL_MS = 300;
const watchNear = (el, set) => {
  if (!el || typeof window.IntersectionObserver !== 'function') {
    set(true);
    return () => {};
  }
  // "Near" counts only once it has lasted DWELL_MS. The page can load
  // before React has rendered anything, so the jump to /#waitlist may come
  // AFTER load: without the dwell, the top-of-page moment before it started
  // the film (measured: 2 MB fetched on /#waitlist all the same).
  let timer = null;
  const io = new window.IntersectionObserver(
    (entries) => {
      clearTimeout(timer);
      if (!entries[entries.length - 1].isIntersecting) {
        set(false);
        return;
      }
      timer = setTimeout(() => set(true), DWELL_MS);
    },
    { rootMargin: '100% 0px' },
  );
  io.observe(el);
  return () => {
    clearTimeout(timer);
    io.disconnect();
  };
};

/** Once the page has loaded: the film never competes with first paint. */
const afterLoad = (fn) => {
  if (document.readyState === 'complete') {
    fn();
    return () => {};
  }
  window.addEventListener('load', fn, { once: true });
  return () => window.removeEventListener('load', fn);
};

const HeroFilm = () => {
  const [reduced, setReduced] = useState(() => {
    const mq = query(REDUCE);
    // A browser that cannot answer gets the still: no answer is not a yes.
    return mq ? mq.matches : true;
  });
  const [cut, setCut] = useState(() => (query(PHONE)?.matches ? 'portrait' : 'wide'));
  const [loaded, setLoaded] = useState(false);
  const [near, setNear] = useState(false);
  // Once started, the film stays: scrolling past it must not drop and
  // re-download it.
  const [started, setStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const video = useRef(null);
  const frame = useRef(null);
  const film = FILM[cut];

  // Follow both settings while the page is open: reduced motion, and the
  // breakpoint (a tablet turned, a window resized across 760 px).
  useEffect(() => {
    const subs = [
      [query(REDUCE), (e) => setReduced(e.matches)],
      [query(PHONE), (e) => setCut(e.matches ? 'portrait' : 'wide')],
    ].filter(([mq]) => mq);
    subs.forEach(([mq, fn]) =>
      mq.addEventListener ? mq.addEventListener('change', fn) : mq.addListener(fn),
    );
    return () =>
      subs.forEach(([mq, fn]) =>
        mq.removeEventListener ? mq.removeEventListener('change', fn) : mq.removeListener(fn),
      );
  }, []);

  useEffect(() => afterLoad(() => setLoaded(true)), []);
  useEffect(() => watchNear(frame.current, setNear), []);
  useEffect(() => {
    if (loaded && near) setStarted(true);
  }, [loaded, near]);

  const showVideo = started && !reduced;

  useEffect(() => {
    if (!showVideo) {
      setPlaying(false);
      return;
    }
    const el = video.current;
    if (!el) return;
    // React does not write the `muted` ATTRIBUTE, and iOS Safari only
    // autoplays a video that is muted. `defaultMuted` IS that attribute (it
    // reflects it); set it and the live state before asking it to play.
    el.defaultMuted = true;
    el.muted = true;
    const p = typeof el.play === 'function' ? el.play() : null;
    if (p && typeof p.catch === 'function') p.catch(() => setPlaying(false));
  }, [showVideo, cut]);

  return (
    <section className="hero-film-section">
      <div ref={frame} className={`hero-film is-${cut}`} aria-hidden="true">
        <img
          className="hero-film-still"
          src={film.endcard}
          alt=""
          width={film.width}
          height={film.height}
          loading={cut === 'wide' ? undefined : 'lazy'}
          decoding="async"
        />
        {showVideo ? (
          <video
            key={cut}
            ref={video}
            className={`hero-film-video${playing ? ' is-playing' : ''}`}
            width={film.width}
            height={film.height}
            poster={film.poster}
            muted
            autoPlay
            loop
            playsInline
            preload="auto"
            disablePictureInPicture
            disableRemotePlayback
            tabIndex={-1}
            onPlaying={() => setPlaying(true)}
          >
            <source src={film.webm} type="video/webm" />
            <source src={film.mp4} type="video/mp4" />
          </video>
        ) : null}
      </div>
    </section>
  );
};

export default HeroFilm;
